/**
 * After a call the rep answered ends with no ticket, the strip offers "Log the call with ..." for
 * the 10 minutes the server set, then drops it quietly. Log it opens capture filled from Dialpad's
 * recap as it lands (2026-11-01). Only the rep who answered gets it (the
 * server decides). It goes as soon as the call's card carries a ticket.
 */
import { type CallCard } from '@lsc/domain';
import { useEffect, useState } from 'react';
import { openCapture } from '../capture/capture-store';
import { Button } from '../ui/button';
import { callLabel, capturePresetFor } from './call-cards';

export interface AfterCallPrompt {
  card: CallCard;
  expiresAt: number;
}

export function AfterCall({ prompt, onDismiss }: { prompt: AfterCallPrompt | null; onDismiss: () => void }) {
  const expiresAt = prompt?.expiresAt ?? 0;
  const expired = prompt !== null && Date.now() >= expiresAt;
  const done = prompt?.card.ticket != null;
  // Hidden at expiry even if the parent keeps passing the same prompt.
  const [timedOut, setTimedOut] = useState<number | null>(null);

  useEffect(() => {
    if (!prompt || done) return;
    const ms = expiresAt - Date.now();
    if (ms <= 0) {
      onDismiss();
      return;
    }
    const t = setTimeout(() => {
      setTimedOut(expiresAt);
      onDismiss();
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [prompt, done, expiresAt, onDismiss]);

  if (!prompt || expired || done || timedOut === expiresAt) return null;
  return (
    <div role="status" className="flex items-center gap-3 border-b border-line bg-surface-2 px-4 py-2 text-body">
      <span className="min-w-0 flex-1 truncate text-ink">{`Log the call with ${callLabel(prompt.card)}`}</span>
      <Button
        variant="primary"
        onClick={() => {
          openCapture(capturePresetFor(prompt.card));
          onDismiss();
        }}
      >
        Log it
      </Button>
      <Button variant="ghost" onClick={onDismiss}>
        Not now
      </Button>
    </div>
  );
}
